import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { ESymbol } from '../../pdf/draw/symbol';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/transforms';
import '../../pdf/draw/symbol';

/**
 * Sachy (Chess) - Chess board with piece cards
 *
 * Layout: 2 portrait pages.
 * Page 1: 8x8 chessboard with coordinate labels (A-H, 1-8).
 * Page 2: Piece cards to cut out - 4 rows of 8 cards each
 *   (white pawns, white pieces, black pawns, black pieces).
 * Each card shows the piece image and chess symbol.
 *
 * Migrated from C# EduFrame.Img10.Generators.Sachy
 */
export class Sachy implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 6;
  }
  countMin(): number {
    return 6;
  }
  countMax(): number {
    return 6;
  }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const border = PdfUnit.FromMillimeters(15);

    const size = (PdfSize.A4.width - 2 * margin - 2 * border) / 8;

    const background = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1));
    const borderRect = new PdfRect(
      margin,
      (PdfSize.A4.height - 8 * size - 2 * margin - 2 * border) / 2,
      8 * size + 2 * border,
      8 * size + 2 * border
    );
    const tableRect = new PdfRect(borderRect.x + border, borderRect.y + border, 8 * size, 8 * size);
    // Page 1: Chess board
    const page1 = pdf.addPage();

    page1.drawRectangle(null, background, borderRect);

    for (let x = 0; x < 8; x++) {
      for (let y = 0; y < 8; y++) {
        const r = new PdfRect(tableRect.x + x * size, tableRect.y + y * size, size, size);

        if ((x + y) % 2 === 0) {
          page1.drawRectangle(null, PdfBrush.White, r);
        } else {
          page1.drawRectangle(null, PdfBrush.Black, r);
        }
      }
    }

    // Coordinate labels
    for (let i = 0; i < 8; i++) {
      const r1 = new PdfRect(tableRect.x + i * size, borderRect.y, size, border);
      const r2 = new PdfRect(
        tableRect.x + i * size,
        borderRect.y + borderRect.height - border,
        size,
        border
      );
      const r3 = new PdfRect(borderRect.x, tableRect.y + i * size, border, size);
      const r4 = new PdfRect(
        borderRect.x + borderRect.width - border,
        tableRect.y + i * size,
        border,
        size
      );

      const a = String.fromCharCode('A'.charCodeAt(0) + i);
      const n = `${8 - i}`;

      page1.save();
      page1.rotateAtTransform(180, new PdfPoint(r1.centerX, r1.centerY));
      page1.drawString(a, GUtil.baseFont, PdfBrush.Black, null, r1, ETextAlign.Center);
      page1.restore();
      page1.drawString(a, GUtil.baseFont, PdfBrush.Black, null, r2, ETextAlign.Center);

      page1.save();
      page1.rotateAtTransform(180, new PdfPoint(r4.centerX, r4.centerY));
      page1.drawString(n, GUtil.baseFont, PdfBrush.Black, null, r4, ETextAlign.Center);
      page1.restore();
      page1.drawString(n, GUtil.baseFont, PdfBrush.Black, null, r3, ETextAlign.Center);
    }

    // Page 2: Piece cards
    const page2 = pdf.addPage();

    // White pawns, white pieces, black pawns, black pieces
    const drawRow = (symbols: ESymbol[], indexes: number[], y: number, white: boolean) => {
      for (let i = 0; i < symbols.length; i++) {
        const r = new PdfRect(margin + i * size, margin + y * size, size, size);
        const s = size / 4;

        const b = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, white ? 0.1 : 0.8));
        page2.drawRectangle(null, b, r);

        if (indexes[i] < opts.list.length) {
          page2.drawImageCenter(opts.list[indexes[i]].image, r);
        }

        const r1 = new PdfRect(r.x + r.width - s, r.y, s, s);
        const r2 = new PdfRect(r.x, r.y + r.height - s, s, s);

        page2.drawRectangle(null, b, r1);
        page2.drawRectangle(null, b, r2);

        const textBrush = white ? PdfBrush.Black : PdfBrush.White;

        page2.drawSymbol(symbols[i], textBrush, r1, 12, ETextAlign.Center);
        page2.drawSymbol(symbols[i], textBrush, r2, 12, ETextAlign.Center);
        page2.drawSymbol(symbols[i], textBrush, r, 48, ETextAlign.Center);

        page2.drawRectangle(GUtil.cutPen, null, r);
      }
    };

    drawRow(
      [
        ESymbol.WhitePawn,
        ESymbol.WhitePawn,
        ESymbol.WhitePawn,
        ESymbol.WhitePawn,
        ESymbol.WhitePawn,
        ESymbol.WhitePawn,
        ESymbol.WhitePawn,
        ESymbol.WhitePawn,
      ],
      [0, 0, 0, 0, 0, 0, 0, 0],
      0,
      true
    );
    drawRow(
      [
        ESymbol.WhiteRook,
        ESymbol.WhiteKnight,
        ESymbol.WhiteBishop,
        ESymbol.WhiteKing,
        ESymbol.WhiteQueen,
        ESymbol.WhiteBishop,
        ESymbol.WhiteKnight,
        ESymbol.WhiteRook,
      ],
      [1, 2, 3, 4, 5, 3, 2, 1],
      1,
      true
    );

    drawRow(
      [
        ESymbol.BlackPawn,
        ESymbol.BlackPawn,
        ESymbol.BlackPawn,
        ESymbol.BlackPawn,
        ESymbol.BlackPawn,
        ESymbol.BlackPawn,
        ESymbol.BlackPawn,
        ESymbol.BlackPawn,
      ],
      [0, 0, 0, 0, 0, 0, 0, 0],
      3,
      false
    );
    drawRow(
      [
        ESymbol.BlackRook,
        ESymbol.BlackKnight,
        ESymbol.BlackBishop,
        ESymbol.BlackKing,
        ESymbol.BlackQueen,
        ESymbol.BlackBishop,
        ESymbol.BlackKnight,
        ESymbol.BlackRook,
      ],
      [1, 2, 3, 4, 5, 3, 2, 1],
      4,
      false
    );
  }
}
